'use client';

import { createAuthenticationAdapter } from '@rainbow-me/rainbowkit';
import crypto from 'crypto';
import { add } from 'date-fns';
import { SiweMessage } from 'siwe';
import { AsyncActionResponse, CommonLocals } from '@pioz/mlm-common';

type AuthenticationAdapter = ReturnType<typeof createAuthenticationAdapter<SiweMessage>>;
type AuthenticationAdapterFactory = (params: {
  locale: CommonLocals;
  signMessage: string;
  onSuccess: (to: string) => void;
  onError: (error: { code: number; message: string }) => void;
  loginAction: (locale: CommonLocals, data: FormData) => AsyncActionResponse<{ redirect: string }>;
}) => AuthenticationAdapter;

export const authenticationAdapter: AuthenticationAdapterFactory = ({
  locale,
  signMessage,
  onSuccess,
  onError,
  loginAction,
}: {
  locale: CommonLocals;
  signMessage: string;
  onSuccess: (to: string) => void;
  onError: (error: { code: number; message: string }) => void;
  loginAction: (locale: CommonLocals, data: FormData) => AsyncActionResponse<{ redirect: string }>;
}) =>
  createAuthenticationAdapter({
    getNonce: async () => {
      return crypto.randomBytes(4).toString('hex');
    },
    createMessage: ({ nonce, address, chainId }) => {
      return new SiweMessage({
        domain: window.location.host,
        address,
        statement: signMessage,
        uri: window.location.origin,
        version: '1',
        chainId,
        expirationTime: add(new Date(), { minutes: 5 }).toISOString(),
        nonce,
      });
    },
    getMessageBody: ({ message }) => {
      return message.prepareMessage();
    },
    verify: async ({ message, signature }) => {
      const formData = new FormData();
      formData.append('message', encodeURI(message.prepareMessage()));
      formData.append('signature', signature);
      formData.append('address', message.address);

      const registrationkind = process.env.NEXT_PUBLIC_REGISTER_TYPE;
      if (registrationkind) formData.append('registrationkind', registrationkind);

      const verifyRes = await loginAction(locale, formData);

      if ('error' in verifyRes) {
        onError(verifyRes.error);
      } else {
        onSuccess(verifyRes.success.redirect);
      }

      return true;
    },
    signOut: async () => {
      await fetch('/api/logout');
    },
  });
